import {
  BooleanInput,
  DateInput,
  Labeled,
  NumberInput,
  ReferenceInput,
  ReferenceArrayInput,
  SelectInput,
  SelectArrayInput,
  useTranslate,
  useInput,
  useI18nProvider,
  TextInput,
} from "react-admin";
import { Button, IconButton } from "@mui/material";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { InputGuesser } from "@api-platform/admin";
import { useFormContext, useWatch } from "react-hook-form";

import CodeEditor from "@uiw/react-textarea-code-editor";
import ClinicalTrialConfigurationField from "./utils/ClinicalTrialConfigurationField";

const WorkflowConfigurationInput = (props: any) => {
  const t = useTranslate();
  document.documentElement.setAttribute("data-color-mode", "dark");
  const { onChange, onBlur, ...rest } = props;
  const { field } = useInput({
    onChange,
    onBlur,
    ...props,
  });
  const preset = useWatch({ name: "preset" });
  var configuration = field?.value;
  if (typeof field?.value == "object" && field?.value !== null) {
    configuration = JSON.stringify(field.value);
  }

  return (
    preset == null && (
      <Labeled
        label={t("resources.analyzis_groups.fields.workflowConfiguration")}
      >
        <CodeEditor
          {...rest}
          {...field}
          placeholder={configuration}
          value={configuration}
          padding={15}
          language="json"
          style={{
            fontSize: 12,
          }}
        />
      </Labeled>
    )
  );
};

const AnalysisGroupFields = () => {
  const t = useTranslate();
  return (
    <fieldset className="border px-4 border-stone-600">
      <legend className="text-lg p-2">
        {t("resources.analyzis_groups.fieldGroup.mandatoryFields")}
      </legend>
      <div className="flex gap-4">
        <InputGuesser source="name" isRequired />
        <ReferenceInput source="organization.id" reference="organizations">
          <SelectInput optionText="name" source="organization" isRequired />
        </ReferenceInput>
      </div>
      <ReferenceArrayInput
        source="users"
        reference="users"
        sort={{ field: "email", order: "ASC" }}
      >
        <SelectArrayInput
          parse={(value) => value && value?.map((v) => ({ id: v }))}
          format={(value) => value && value?.map((v) => v.id)}
          optionText="email"
          isRequired
          defaultValue={[]}
        />
      </ReferenceArrayInput>
    </fieldset>
  );
};

const AnalysisGroupReportFields = () => {
  const t = useTranslate();
  const i18nProvider = useI18nProvider();
  const locales = i18nProvider.getLocales();
  return (
    <fieldset className="border px-4 border-stone-600">
      <legend className="text-lg p-2">
        {t("resources.analyzis_groups.fieldGroup.report")}
      </legend>
      <div className="flex gap-4">
        <InputGuesser source="pathology" />
        <SelectInput
          source="locale"
          choices={locales}
          optionText="name"
          optionValue="locale"
          emptyText="-- Default --"
        />
      </div>
      <div className="flex gap-4">
        <BooleanInput source="synthetic" label={t("report.type.syn")} />
        <BooleanInput source="qualityControl" label={t("report.type.qc")} />
      </div>
    </fieldset>
  );
};

const AnalysisGroupPipelineFields = () => {
  const t = useTranslate();
  return (
    <fieldset className="border px-4 border-stone-600">
      <legend className="text-lg p-2">
        {t("resources.analyzis_groups.fieldGroup.pipeline")}
      </legend>
      <div className="flex gap-4">
        <InputGuesser source="preset" isRequired />
        <InputGuesser source="retention" />
      </div>
      <div className="flex gap-4 pb-4">
        <WorkflowConfigurationInput source="workflowConfiguration" />
      </div>
      <div className="flex gap-4">
        <BooleanInput source="allowDerived" />
        <BooleanInput source="anonymize" />
        <BooleanInput source="keepTemporaryFiles" />
      </div>
    </fieldset>
  );
};

const AnalysisGroupClinicalFields = () => {
  const t = useTranslate();
  const clinicalTrialConfiguration = useWatch({
    name: "clinicalTrialConfiguration",
  });
  const { setValue } = useFormContext();
  if (!clinicalTrialConfiguration) {
    return (
      <Button
        type="button"
        className="!mt-2"
        variant="outlined"
        onClick={() => setValue("clinicalTrialConfiguration", {})}
      >
        {" "}
        Add a clinical trials
      </Button>
    );
  }
  return (
    <fieldset className="border px-4 border-stone-600 relative">
      <legend className="text-lg p-2">
        {t("resources.analyzis_groups.fieldGroup.clinicalTrials")}
      </legend>
      <IconButton
        size="small"
        className="-top-8 -right-2"
        sx={{
          position: "absolute",
          backgroundColor: "#121212",
          "&:hover": { backgroundColor: "#121212" },
        }}
        aria-label="remove"
        title="Remove Clinical trials"
        onClick={() => setValue("clinicalTrialConfiguration", null)}
        color="error"
      >
        <RemoveCircleOutlineIcon />
      </IconButton>
      <div className="flex gap-4">
        <TextInput source="clinicalTrialConfiguration.sponsor" isRequired />
        <TextInput source="clinicalTrialConfiguration.molecule" />
        <DateInput source="clinicalTrialConfiguration.expected_start_date" />
        <DateInput source="clinicalTrialConfiguration.expected_end_date" />
      </div>
      <div className="flex gap-4">
        <NumberInput
          source="clinicalTrialConfiguration.expected_number_of_subjects"
          isRequired
        />
        <NumberInput
          source="clinicalTrialConfiguration.expected_number_of_visits"
          isRequired
        />
      </div>
      <ClinicalTrialConfigurationField source="clinicalTrialConfiguration" />
    </fieldset>
  );
};

export const AnalysisGroupForm = () => {
  return (
    <>
      <AnalysisGroupFields />
      <AnalysisGroupPipelineFields />
      <AnalysisGroupReportFields />
      <AnalysisGroupClinicalFields />
    </>
  );
};
